/**
 * Forms client — mirrors `apps/api/src/domains/forms/routes.ts` verbatim.
 *
 * Fourteen routes carrying a complete author → review → approve → publish →
 * assign → respond lifecycle, and until now nothing on this side could call any
 * of them. The whole domain was reachable only by curl.
 *
 * FOUR CONTRACT RULES A SCREEN MUST NOT REDISCOVER THE HARD WAY.
 *
 * 1. NOTHING PUBLISHES THAT A HUMAN DID NOT APPROVE, and the gate is
 *    structural rather than polite. `publish` takes NO BODY: no approval id, no
 *    force flag, nothing a caller could pass to skip the check. The server finds
 *    the approval, and `form_version_publish_needs_approval_ck` refuses the row
 *    if there isn't one. A refusal comes back as 409 with
 *    `reason: 'unapproved_publication'` — the one machine-readable conflict code
 *    in this domain (see {@link isUnapprovedPublication}).
 *
 * 2. AN AI-AUTHORED VERSION HAS NO HUMAN AUTHOR, AND SAYS SO. Passing
 *    `aiModelRef` sets `authorship` to `ai_drafted` (or `ai_assisted` with
 *    `aiAssisted: true`) and leaves `authoredBy` NULL. That null is the CLAIM —
 *    "no person wrote this" — not a missing join, and a screen that renders it
 *    as "Unknown author" has erased the distinction the domain exists for.
 *
 * 3. AN UNAPPROVED MAPPING IS INERT. `FormSignalMappingView.mappingApprovalId`
 *    is null until somebody with `knowledge:manage` approves it, and an inert
 *    mapping emits nothing however well it matches. `submit` reports the ones
 *    that WOULD have fired in `unapprovedMatches`, which is the whole point:
 *    silence with a receipt, rather than silence.
 *
 * 4. A RESPONSE YOU MAY NOT READ AND A RESPONSE THAT DOES NOT EXIST ARE THE
 *    SAME 404, with the same constant message. That is deliberate
 *    anti-enumeration (`response-reach.ts`), so a `NotFoundError` from
 *    {@link response} is NOT evidence the response is absent. And holding
 *    `forms:respond` confers no read at all: a worker can submit a response and
 *    then be refused their own — the write path performs no reach check, and the
 *    read path is per-row.
 *
 * EVERY ROUTE ALSO NEEDS `directory:read`, on reads and writes alike, because a
 * form names the people who authored and approved it. The server checks it as a
 * second gate and answers 403 with that key.
 */
import { get, post } from './transport.js';
// ----------------------------------------------------------------- constants
/** The route gates, re-exported so a screen and a route cannot drift apart. */
export const FORMS_READ = 'knowledge:read';
export const FORMS_AUTHOR = 'knowledge:write';
export const FORMS_APPROVE = 'knowledge:publish';
export const FORMS_MAP_SIGNAL = 'knowledge:manage';
export const FORMS_RESPOND = 'forms:respond';
export const FORMS_READ_RESPONSES = 'forms:read_responses';
/** Widens response reach; NOT a route gate, and only in concert with `directory:read_all`. */
export const FORMS_READ_RESPONSES_ALL = 'forms:read_responses_all';
/** The second gate on every route in this domain. */
export const FORMS_DIRECTORY_READ = 'directory:read';
/**
 * The OTHER half of the organization-wide widener. Not a forms key at all.
 *
 * Named here because {@link readsEveryResponse} needs it, and naming it beside
 * the conjunction is what stops a screen writing the `&&` itself.
 */
export const FORMS_DIRECTORY_READ_ALL = 'directory:read_all';
/**
 * MAY THIS CALLER READ EVERY RESPONSE IN THE ORGANIZATION — the client-side
 * MIRROR of `forms/response-reach.ts:formResponseReader`, and the only place on
 * this side that composes the two keys.
 *
 * IT IS A CONJUNCTION, AND THE SERVER SAYS WHY. `forms:read_responses_all`
 * alone does NOT widen anything: `formResponseReader` returns
 * `readsEveryResponse: formsReadResponsesAll && directoryReadAll`, because
 * reading every form response is reading every participant — the row carries
 * `subject_client_id` and the answers carry the narrative — and lane L115 found
 * that a `*:read_all` key can be granted from the People screen without any
 * participant authority beside it. A holder of the widener alone falls back to
 * caseload reach on the server.
 *
 * A SCREEN MUST NOT REIMPLEMENT REACH, AND THIS IS NOT REACH. Which ROWS answer
 * is decided per row, at read time, by `formResponseReachMap` against
 * `directory/client-reach.ts`, and no client can compute it: the inputs are the
 * response's `subject_client_id` and the caller's open assignments, neither of
 * which is on this side of the wire. What this answers is the strictly weaker,
 * genuinely client-side question "what may this screen TELL THE READER about
 * the scope of what they are looking at" — the `ScopeNote` sentence. Using it
 * to decide whether to SHOW a response would be exactly the reimplementation
 * the server's header forbids.
 */
export function readsEveryResponse(has) {
    return has(FORMS_READ_RESPONSES_ALL) && has(FORMS_DIRECTORY_READ_ALL);
}
/** A customer-defined signal key. `library` keys must name an `OPERATIONAL_SIGNALS` id. */
export const CUSTOM_SIGNAL_KEY_PATTERN = /^custom:[a-z0-9][a-z0-9_-]{1,63}$/;
/** Only `select` and `multiselect` may carry options; every other type may not. */
export function fieldTakesOptions(dataType) {
    return dataType === 'select' || dataType === 'multiselect';
}
/** `is_blank`/`is_not_blank` take no value; every other operator requires one. */
export function operatorTakesValue(operator) {
    return operator !== 'is_blank' && operator !== 'is_not_blank';
}
/** Would this mapping emit anything? Only an approved one ever does. */
export function isInert(mapping) {
    return mapping.mappingApprovalId === null || !mapping.active;
}
/** `GET /api/forms` — needs `knowledge:read` + `directory:read`. */
export async function definitions(query = {}, options) {
    const body = await get('/api/forms', {
        ...options,
        query: { ...query },
    });
    return body.definitions;
}
/** `POST /api/forms` → 201. Needs `knowledge:write`. */
export async function createDefinition(input, options) {
    const body = await post('/api/forms', input, options);
    return body.definition;
}
/**
 * `GET /api/forms/:id` — the definition with every version, approval, mapping
 * and history entry. Returned BARE, not in an envelope.
 */
export function detail(id, options) {
    return get(`/api/forms/${id}`, options);
}
/** `POST /api/forms/:id/versions` → 201. A new version of this definition. */
export async function draftVersion(definitionId, input, options) {
    const body = await post(`/api/forms/${definitionId}/versions`, input, options);
    return body.version;
}
/**
 * `POST /api/forms/versions/:id/revise` → 201, and the id is the SOURCE
 * VERSION, not the definition.
 *
 * A NEW ROW, ALWAYS. An approved version is frozen against the content hash its
 * approval names, so "editing" it is a new version that needs its own approval.
 */
export async function reviseVersion(sourceVersionId, input, options) {
    const body = await post(`/api/forms/versions/${sourceVersionId}/revise`, input, options);
    return body.version;
}
/** `GET /api/forms/versions/:id`. */
export async function version(id, options) {
    const body = await get(`/api/forms/versions/${id}`, options);
    return body.version;
}
/** `POST /api/forms/versions/:id/review` — no body. Moves draft → review. */
export async function submitForReview(id, options) {
    const body = await post(`/api/forms/versions/${id}/review`, {}, options);
    return body.version;
}
/**
 * `POST /api/forms/versions/:id/decision` — needs `knowledge:publish`.
 *
 * Records a HUMAN decision bound to this version's exact content hash and
 * authorship. `aiReviewStatement` is required when approving a version no person
 * wrote.
 */
export async function recordDecision(id, input, options) {
    const body = await post(`/api/forms/versions/${id}/decision`, input, options);
    return body.version;
}
/**
 * `POST /api/forms/versions/:id/publish` — needs `knowledge:publish`.
 *
 * NO BODY, AND THAT IS THE DESIGN. There is no approval id to pass and no force
 * flag to set, so there is no argument a caller could supply to publish
 * something nobody approved. A 409 carrying `reason: 'unapproved_publication'`
 * is the server declining; see {@link isUnapprovedPublication}.
 */
export async function publish(id, options) {
    const body = await post(`/api/forms/versions/${id}/publish`, {}, options);
    return body.version;
}
/** `POST /api/forms/versions/:id/mappings` → 201. Proposes; does not approve. */
export async function proposeMapping(versionId, input, options) {
    const body = await post(`/api/forms/versions/${versionId}/mappings`, input, options);
    return body.mapping;
}
/** `POST /api/forms/mappings/:id/approve` — needs `knowledge:manage`. Makes it fire. */
export async function approveMapping(mappingId, input, options) {
    const body = await post(`/api/forms/mappings/${mappingId}/approve`, input, options);
    return body.mapping;
}
/** `POST /api/forms/:id/assignments` → 201. Needs `knowledge:write`. */
export async function assign(definitionId, input, options) {
    const body = await post(`/api/forms/${definitionId}/assignments`, input, options);
    return body.assignment;
}
/**
 * `POST /api/forms/responses` → 201, BARE (not in an envelope).
 *
 * Needs `forms:respond`, which grants NO READ. A responder who does not also
 * hold `forms:read_responses` cannot open what they just submitted — the
 * outcome returned here is the only view of it they get.
 */
export function submit(input, options) {
    return post('/api/forms/responses', input, options);
}
/**
 * `GET /api/forms/responses/:id` — needs `forms:read_responses`. Returned BARE.
 *
 * A 404 HERE MEANS EITHER "not yours" OR "not there", and the server will not
 * say which: the message is a constant and the two paths are indistinguishable
 * by construction. Do not render it as "this was deleted".
 */
export function response(id, options) {
    return get(`/api/forms/responses/${id}`, options);
}
// ------------------------------------------------------------------- errors
/**
 * Was this 409 the publication gate refusing?
 *
 * `reason: 'unapproved_publication'` is the ONE machine-readable conflict code
 * in this domain. Every other 409 — a version in the wrong state, a form already
 * assigned — carries no `reason` at all, so a screen distinguishing among those
 * has only the message. Reading the code where there is one is what keeps the
 * important refusal from being lumped in with the rest.
 *
 * Structural and status-guarded, for the reason `isRestrictedByEligibility` is:
 * the error may not be this package's class.
 */
export function isUnapprovedPublication(err) {
    if (typeof err !== 'object' || err === null)
        return false;
    if (err.status !== 409)
        return false;
    const body = err.body;
    if (typeof body !== 'object' || body === null)
        return false;
    return body.reason === 'unapproved_publication';
}
/**
 * The server's per-issue list from a 400, when it sent one.
 *
 * Two different 400s carry `issues`: a Zod failure (`{ path, message }` pairs)
 * and a `FormValidationError` (plain strings saying how a submission does not
 * fit the version it answers). Both are worth showing next to the field they are
 * about, and both are dropped by a screen that reads only `message`.
 */
export function formIssues(err) {
    if (typeof err !== 'object' || err === null)
        return [];
    const body = err.body;
    if (typeof body !== 'object' || body === null)
        return [];
    const raw = body.issues;
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const issue of raw) {
        if (typeof issue === 'string') {
            out.push(issue);
            continue;
        }
        if (typeof issue === 'object' && issue !== null) {
            const path = issue.path;
            const message = issue.message;
            if (typeof message !== 'string')
                continue;
            out.push(typeof path === 'string' && path !== '' ? `${path}: ${message}` : message);
        }
    }
    return out;
}
//# sourceMappingURL=forms.js.map